import { useState, useEffect, useCallback, useMemo } from 'react';
import { Analytics } from "@vercel/analytics/react";
import moroccoAdjustments from './moroccoAdjustments.json';
import { 
  isPushNotificationSupported, 
  isSubscribedToPush, 
  subscribeToPush, 
  unsubscribeFromPush, 
  updateSubscriptionMetadata 
} from './utils/pushNotifications';

const SunnahFastingCalendar = () => {
  const [calendarSource, setCalendarSource] = useState(() => {
    return localStorage.getItem('calendarSource') || 'morocco';
  });
  const [islamicCalendarData, setIslamicCalendarData] = useState([]);
  const [, forceUpdate] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved ? JSON.parse(saved) : {};
  });
  const [isStandalone, setIsStandalone] = useState(false);
  const [showMobileInstructions, setShowMobileInstructions] = useState(false);
  
  // Dynamic current date that updates when month changes
  const [currentDate, setCurrentDate] = useState(() => new Date());

  // Clean up notifications state to ensure only one calendar source is enabled
  useEffect(() => {
    const enabledSources = Object.keys(notificationsEnabled).filter(key => notificationsEnabled[key]);
    console.log('🔍 Current notifications state:', notificationsEnabled);
    console.log('🔍 Enabled sources:', enabledSources);
    console.log('🔍 Current calendar source:', calendarSource);
    
    if (enabledSources.length > 1) {
      console.log('⚠️ Multiple notification sources enabled, cleaning up...');
      // If multiple sources are enabled, keep only the current calendar source
      const cleanedData = { morocco: false, saudi: false };
      if (enabledSources.includes(calendarSource)) {
        cleanedData[calendarSource] = true;
      }
      console.log('✅ Cleaned data:', cleanedData);
      setNotificationsEnabled(cleanedData);
      localStorage.setItem('notificationsEnabled', JSON.stringify(cleanedData));
    }
  }, [calendarSource, notificationsEnabled]);

  // Service Worker and Notification Setup
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: '/' })
        .then((registration) => {
          console.log('Service Worker registered:', registration);
          
          // Set up BroadcastChannel for communication with service worker
          const channel = new BroadcastChannel('calendar-source-channel');
          channel.onmessage = (event) => {
            if (event.data.type === 'GET_CALENDAR_SOURCE') {
              channel.postMessage({
                type: 'CALENDAR_SOURCE_RESPONSE',
                calendarSource: calendarSource,
                notificationsEnabled: notificationsEnabled
              });
            }
          };
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }


    // Check if app is already installed (standalone mode)
    const checkStandalone = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true ||
                          document.referrer.includes('android-app://');
      setIsStandalone(isStandalone);
    };
    
    checkStandalone();
    window.matchMedia('(display-mode: standalone)').addEventListener('change', checkStandalone);

    // Check notification permission on load and periodically
    const checkPermission = () => {
      if ('Notification' in window) {
        console.log('Current notification permission:', Notification.permission);
        setNotificationPermission(Notification.permission);
      }
    };
    
    checkPermission();
    
    // Check permission every 2 seconds to catch Safari updates
    const permissionChecker = setInterval(checkPermission, 2000);
    
    return () => {
      clearInterval(permissionChecker);
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkStandalone);
    };
  }, [calendarSource, notificationsEnabled]);

  // Update push subscription when calendar source changes
  useEffect(() => {
    const updatePushSubscription = async () => {
      if (notificationsEnabled[calendarSource] && notificationPermission === 'granted') {
        try {
          const isSubscribed = await isSubscribedToPush();
          if (isSubscribed) {
            await updateSubscriptionMetadata(calendarSource);
            console.log('📱 Updated push subscription for calendar source:', calendarSource);
          }
        } catch (error) {
          console.error('Failed to update push subscription:', error);
        }
      }
    };

    updatePushSubscription();
  }, [calendarSource, notificationsEnabled, notificationPermission]);


  // Request notification permission and set up push subscription
  const requestNotificationPermission = async () => {
    if (!isPushNotificationSupported()) {
      console.error('Push notifications not supported');
      return;
    }

    try {
      console.log('📱 Requesting notification permission and setting up push...');
      
      // Subscribe to push notifications (this handles permission request internally)
      await subscribeToPush();
      
      // Update local state
      setNotificationPermission('granted');
      const newNotificationsEnabled = { morocco: false, saudi: false, [calendarSource]: true };
      setNotificationsEnabled(newNotificationsEnabled);
      localStorage.setItem('notificationsEnabled', JSON.stringify(newNotificationsEnabled));
      
      console.log('📱 Push notifications successfully set up');
      
    } catch (error) {
      console.error('Failed to set up push notifications:', error);
      setNotificationPermission('denied');
    }
  };



  // Platform and browser detection
  const getPlatformInfo = () => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isiPad = /iPad/.test(userAgent);
    const isiPhone = /iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    
    // Detect iPadOS that reports as macOS (Safari Desktop mode or iPadOS 13+)
    const isiPadOS = /Macintosh/.test(userAgent) && 'ontouchend' in document;
    
    // Detect Android tablets - they usually don't have "Mobile" in user agent
    const isAndroidTablet = /Android/.test(userAgent) && !/Mobile/.test(userAgent);
    
    // Detect any tablet (iPad, iPadOS, or Android tablet)
    const isTablet = isiPad || isiPadOS || isAndroidTablet;
    
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/CriOS/.test(userAgent) && !/FxiOS/.test(userAgent);
    const isChrome = /Chrome/.test(userAgent) && !/CriOS/.test(userAgent); // Exclude Chrome on iOS
    const isChromeIOS = /CriOS/.test(userAgent); // Chrome on iOS
    const isFirefox = /Firefox/.test(userAgent);
    const isEdge = /Edg/.test(userAgent);
    
    return { isIOS, isiPad, isiPhone, isAndroid, isiPadOS, isAndroidTablet, isTablet, isSafari, isChrome, isChromeIOS, isFirefox, isEdge };
  };




  // Detect if mobile/tablet browser (not PWA) - includes all tablets and phones
  const isMobileBrowser = () => {
    const platform = getPlatformInfo();
    const result = (platform.isiPhone || platform.isTablet || (platform.isAndroid && !platform.isAndroidTablet)) && !isStandalone;
    console.log('🔍 Mobile/Tablet detection:', {
      platform,
      isStandalone,
      result,
      includesAllTablets: true,
      userAgent: navigator.userAgent
    });
    return result;
  };

  // Handle notification toggle - with proper push subscription management
  const handleNotificationToggle = async (checked) => {
    console.log('🔄 Toggle clicked:', {
      checked,
      isMobile: isMobileBrowser(),
      isStandalone,
      showInstructions: showMobileInstructions
    });
    
    if (isMobileBrowser()) {
      // On mobile browser, just toggle instructions
      console.log('📱 Mobile browser - toggling instructions');
      setShowMobileInstructions(checked);
      return;
    }
    
    // PWA behavior - proper push subscription logic
    console.log('💻 PWA/Desktop - push subscription logic');
    
    if (checked) {
      // Enable notifications
      if (notificationPermission === 'default') {
        await requestNotificationPermission();
      } else if (notificationPermission === 'granted') {
        try {
          // Check if already subscribed, if not subscribe
          const isSubscribed = await isSubscribedToPush();
          if (!isSubscribed) {
            await subscribeToPush();
          } else {
            // Update subscription metadata with current calendar source
            await updateSubscriptionMetadata(calendarSource);
          }
          
          // Update local state
          const newNotificationsEnabled = { morocco: false, saudi: false, [calendarSource]: true };
          setNotificationsEnabled(newNotificationsEnabled);
          localStorage.setItem('notificationsEnabled', JSON.stringify(newNotificationsEnabled));
          
        } catch (error) {
          console.error('Failed to enable push notifications:', error);
        }
      }
    } else {
      // Disable notifications
      try {
        await unsubscribeFromPush();
        const newNotificationsEnabled = { ...notificationsEnabled, [calendarSource]: false };
        setNotificationsEnabled(newNotificationsEnabled);
        localStorage.setItem('notificationsEnabled', JSON.stringify(newNotificationsEnabled));
        
      } catch (error) {
        console.error('Failed to disable push notifications:', error);
      }
    }
  };

  // Helper function to get fasting labels for upcoming date
  const getFastingLabelsForUpcomingDate = useCallback(async (date) => {
    try {
      // Use the same API call logic as the main app
      // Format date as DD-MM-YYYY for the API
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const dateString = `${day}-${month}-${year}`;
      const adjustment = calendarSource === 'morocco' ? 1 : 0;
      const endpoint = `https://api.aladhan.com/v1/gToH/${dateString}?adjustment=${adjustment}`;
      console.log('🌐 API call:', endpoint);
      
      const response = await fetch(endpoint);
      console.log('📡 API response status:', response.status, response.statusText);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 API response data:', data);
        if (data && data.data && data.data.hijri) {
          const islamicDate = {
            day: parseInt(data.data.hijri.day),
            month: {
              number: parseInt(data.data.hijri.month.number),
              year: parseInt(data.data.hijri.year)
            }
          };
          
          // Use the same fasting logic as getFastingDayLabels
          const labels = [];
          const dayOfWeek = date.getDay();
          const islamicDay = islamicDate.day;
          const islamicMonth = islamicDate.month.number;

          // Forbidden fasting days (Sahih Muslim, Abu Sa'id al-Khudri):
          // Eid al-Fitr (1 Shawwal), Eid al-Adha (10 Dhul Hijjah),
          // and the three days of Tashreeq (11-13 Dhul Hijjah).
          // Suppress all Sunnah labels — fasting is not permitted on these days.
          const isForbidden =
            (islamicMonth === 10 && islamicDay === 1) ||
            (islamicMonth === 12 && islamicDay >= 10 && islamicDay <= 13);
          if (isForbidden) {
            return [];
          }

          // Monday fasting
          if (dayOfWeek === 1) {
            labels.push({ text: 'Monday' });
          }

          // Thursday fasting
          if (dayOfWeek === 4) {
            labels.push({ text: 'Thursday' });
          }

          // White Days (13th, 14th, 15th of any Islamic month)
          if (islamicDay === 13 || islamicDay === 14 || islamicDay === 15) {
            labels.push({ text: 'White Day' });
          }

          // Day of Arafah (9th Dhul Hijjah)
          if (islamicMonth === 12 && islamicDay === 9) {
            labels.push({ text: 'Day of Arafah' });
          }

          // Day of Ashura (10th Muharram)
          if (islamicMonth === 1 && islamicDay === 10) {
            labels.push({ text: 'Day of Ashura' });
          }

          // First 9 days of Dhul Hijjah (1st-9th Dhul Hijjah)
          if (islamicMonth === 12 && islamicDay >= 1 && islamicDay <= 9) {
            labels.push({ text: 'First 9 Days' });
          }

          // 6 days of Shawwal (2nd-7th Shawwal, excluding Eid day)
          if (islamicMonth === 10 && islamicDay >= 2 && islamicDay <= 7) {
            labels.push({ text: 'Shawwal' });
          }

          return labels;
        }
      } else {
        // API returned error (404, etc.) - treat as failure
        console.log('⚠️ API returned error status, falling back to test data');
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error fetching upcoming Islamic date:', error);
      
      // FOR TESTING: Return fake fasting labels if API fails
      console.log('🧪 API failed, returning fake fasting labels for testing...');
      return [
        { text: 'Monday' },
        { text: 'White Day' }
      ];
    }
    
    return [];
  }, [calendarSource]);

  // Function to check tomorrow's fasting days
  const checkTomorrowFasting = useCallback(async () => {
    console.log('🔔 checkTomorrowFasting called at:', new Date().toLocaleTimeString());
    
    // Debug platform info for Android issues
    const platform = getPlatformInfo();
    console.log('🔍 Platform detection:', {
      userAgent: navigator.userAgent,
      isAndroid: platform.isAndroid,
      isChrome: platform.isChrome,
      isStandalone: window.matchMedia('(display-mode: standalone)').matches,
      notificationPermission: Notification.permission,
      serviceWorkerSupport: 'serviceWorker' in navigator
    });
    
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      console.log('📅 Checking fasting for date:', tomorrow.toDateString());
      
      // Get Islamic date for tomorrow using the same logic as the app
      const tomorrowFastingLabels = await getFastingLabelsForUpcomingDate(tomorrow);
      console.log('🕌 Found fasting labels:', tomorrowFastingLabels);
      
      if (tomorrowFastingLabels.length > 0) {
        const fastingTypes = tomorrowFastingLabels.map(label => label.text).join(', ');
        console.log('✅ Will show notification for:', fastingTypes);
        console.log('🔍 serviceWorker available:', 'serviceWorker' in navigator);
        console.log('🔍 navigator.serviceWorker:', !!navigator.serviceWorker);
        
        // Try service worker notification first (better for Android PWAs), fallback to direct
        console.log('📱 Attempting service worker notification first...');
        try {
          if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;
            console.log('✅ Service worker ready, showing notification via SW');
            await registration.showNotification(`Sunnah fast tomorrow 🌙`, {
              body: `Join thousands making intention tonight.`,
              icon: '/logo192.png',
              badge: '/logo192.png',
              vibrate: [200, 100, 200],
              tag: 'fasting-reminder',
              requireInteraction: true, // Android needs this to show properly
              actions: [
                {
                  action: 'view',
                  title: 'View Calendar'
                }
              ]
            });
            console.log('✅ Service worker notification sent successfully');
          } else {
            throw new Error('Service worker not available');
          }
        } catch (swError) {
          console.error('❌ Service worker notification failed:', swError);
          console.log('📱 Falling back to direct notification...');
          try {
            new Notification(`Sunnah fast tomorrow 🌙`, {
              body: `Join thousands making intention tonight.`,
              icon: '/logo192.png',
              requireInteraction: true
            });
            console.log('✅ Direct notification sent successfully');
          } catch (directError) {
            console.error('❌ Direct notification also failed:', directError);
          }
        }
      } else {
        console.log('❌ No fasting tomorrow. Labels:', tomorrowFastingLabels.length, 'SW:', 'serviceWorker' in navigator);
        
        // FOR TESTING: Show a notification anyway
        console.log('🧪 TEST: Showing test notification anyway...');
        try {
          if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification('Test Notification! 🧪', {
              body: 'This is a test notification - no fasting tomorrow but testing the system!',
              icon: '/logo192.png',
              badge: '/logo192.png',
              requireInteraction: true,
              tag: 'test-notification'
            });
            console.log('✅ Test service worker notification sent successfully');
          } else {
            throw new Error('Service worker not available for test');
          }
        } catch (swError) {
          console.error('❌ Test service worker notification failed:', swError);
          try {
            new Notification('Test Notification! 🧪', {
              body: 'This is a test notification - no fasting tomorrow but testing the system!',
              icon: '/logo192.png',
              requireInteraction: true
            });
            console.log('✅ Test direct notification sent successfully');
          } catch (directError) {
            console.error('❌ Test direct notification failed:', directError);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error checking tomorrow fasting:', error);
    }
  }, [getFastingLabelsForUpcomingDate]);

  // Schedule daily notification check
  useEffect(() => {
    if (!Object.values(notificationsEnabled).some(enabled => enabled) || notificationPermission !== 'granted') return;

    const scheduleNotificationCheck = () => {
      const now = new Date();
      const scheduledTime = new Date();
      
      // Schedule for 7:00 PM (19:00) today, or 7:00 PM tomorrow if it's already past 7:00 PM
      scheduledTime.setHours(19, 0, 0, 0); // 7:00 PM
      
      console.log('🕐 Scheduling notification check for:', scheduledTime.toLocaleString());
      console.log('🌍 User timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
      
      if (now > scheduledTime) {
        // If it's already past 7:00 PM today, schedule for 7:00 PM tomorrow
        scheduledTime.setDate(scheduledTime.getDate() + 1);
      }
      
      const timeUntilCheck = scheduledTime.getTime() - now.getTime();
      
      const timeout = setTimeout(() => {
        // Check for tomorrow's fasting days
        checkTomorrowFasting();
        
        // Set up daily recurring check
        const dailyInterval = setInterval(() => {
          checkTomorrowFasting();
        }, 24 * 60 * 60 * 1000); // Check every 24 hours
        
        // Store the interval ID to clear it later if needed
        localStorage.setItem('notificationInterval', dailyInterval);
        
        return () => clearInterval(dailyInterval);
      }, timeUntilCheck);
      
      // Store the timeout ID to clear it later if needed
      localStorage.setItem('notificationTimeout', timeout);
      
      return () => clearTimeout(timeout);
    };

    // Clear any existing timers
    const existingTimeout = localStorage.getItem('notificationTimeout');
    const existingInterval = localStorage.getItem('notificationInterval');
    if (existingTimeout) clearTimeout(existingTimeout);
    if (existingInterval) clearInterval(existingInterval);

    return scheduleNotificationCheck();
  }, [notificationsEnabled, notificationPermission, calendarSource, checkTomorrowFasting]);


  const calculateIslamicDate = useCallback((gregorianDate) => {
    // Use the date as-is - adjustments are handled by the Morocco adjustment data
    // or API data depending on calendar source
    const calculationDate = gregorianDate;
    
    const calcYear = calculationDate.getFullYear();
    const calcMonth = calculationDate.getMonth() + 1;
    const calcDay = calculationDate.getDate();
    
    let a = Math.floor((14 - calcMonth) / 12);
    let y = calcYear - a;
    let m = calcMonth + 12 * a - 3;
    let jdn = calcDay + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + 1721119;
    
    let islamicJdn = jdn - 1948440;
    
    // Different adjustments based on calendar source
    let adjustment;
    if (calendarSource === 'saudi') {
      // Umm al-Qura uses astronomical calculations, closer to 0 adjustment
      adjustment = 0;
    } else {
      // Morocco uses local sighting, typically -1 for conservative approach
      adjustment = -1;
    }
    
    let islamicYear = Math.floor((islamicJdn * 30 + 10646) / 10631);
    let remainingDays = islamicJdn - Math.floor((islamicYear * 10631 - 10646) / 30) + adjustment;
    
    let islamicMonth = Math.floor((remainingDays * 12 + 325) / 354);
    if (islamicMonth > 12) islamicMonth = 12;
    if (islamicMonth < 1) islamicMonth = 1;
    
    let islamicDay = remainingDays - Math.floor((islamicMonth - 1) * 354 / 12) + 1;
    if (islamicDay > 30) islamicDay = 30;
    if (islamicDay < 1) islamicDay = 1;
    
    return {
      year: Math.max(1, islamicYear),
      month: islamicMonth,
      day: Math.floor(islamicDay)
    };
  }, [calendarSource]);

  // Helper: Calculate Hijri date from Morocco month starts
  const getMoroccoHijriDate = useCallback((gregorianDate) => {
    const monthStarts = moroccoAdjustments.monthStarts || [];
    if (monthStarts.length === 0) return null;

    // Sort month starts by date (newest first)
    const sorted = [...monthStarts].sort((a, b) =>
      new Date(b.gregorianStart) - new Date(a.gregorianStart)
    );

    // Normalise to UTC midnight so JSON date strings (parsed as UTC) and
    // locally-constructed dates (which carry a timezone offset) compare correctly.
    const targetDate = new Date(Date.UTC(
      gregorianDate.getFullYear(),
      gregorianDate.getMonth(),
      gregorianDate.getDate()
    ));

    let monthStart = null;
    for (const ms of sorted) {
      const startDate = new Date(ms.gregorianStart); // already UTC midnight
      if (startDate <= targetDate) {
        monthStart = ms;
        break;
      }
    }

    if (!monthStart) return null;

    // Calculate days since month start
    const startDate = new Date(monthStart.gregorianStart); // UTC midnight
    const daysDiff = Math.floor((targetDate - startDate) / (1000 * 60 * 60 * 24));

    // Calculate Hijri date (assuming 30-day months for simplicity)
    let hijriDay = 1 + daysDiff;
    let hijriMonth = monthStart.hijriMonth;
    let hijriYear = monthStart.hijriYear;

    // Handle month overflow (if more than 30 days from start)
    let overflowed = false;
    while (hijriDay > 30) {
      hijriDay -= 30;
      hijriMonth++;
      overflowed = true;
      if (hijriMonth > 12) {
        hijriMonth = 1;
        hijriYear++;
      }
    }

    // If we overflowed into the next month, look up whether that month has
    // its own confirmed entry — don't inherit the covering month's status.
    let confirmed = monthStart.confirmed;
    if (overflowed) {
      const match = monthStarts.find(
        ms => ms.hijriMonth === hijriMonth && ms.hijriYear === hijriYear
      );
      confirmed = match ? match.confirmed : false;
    }

    return {
      day: hijriDay,
      month: hijriMonth,
      year: hijriYear,
      confirmed
    };
  }, []);

  const getMoroccoAdjustmentStatus = useCallback((gregorianDay) => {
    if (calendarSource !== 'morocco') return null;

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const targetDate = new Date(year, month - 1, gregorianDay);

    const hijri = getMoroccoHijriDate(targetDate);
    if (!hijri) return 'uncertain';

    // Require an explicit entry for the computed hijri month — never inherit
    // a previous month's confirmed status.
    const monthStarts = moroccoAdjustments.monthStarts || [];
    const match = monthStarts.find(
      ms => ms.hijriMonth === hijri.month && ms.hijriYear === hijri.year
    );

    if (!match) return 'uncertain';
    return match.confirmed ? 'confirmed' : 'provisional';
  }, [calendarSource, currentDate, getMoroccoHijriDate]);

  const fetchMonthlyIslamicCalendar = useCallback(async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

    // Morocco: Calculate from month start dates
    if (calendarSource === 'morocco') {
      console.log(`🇲🇦 Calculating Morocco calendar for ${month}/${year}`);

      const daysInMonth = new Date(year, month, 0).getDate();
      const calendarDays = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const gregorianDate = new Date(year, month - 1, day);
        const hijri = getMoroccoHijriDate(gregorianDate);

        if (hijri) {
          calendarDays.push({
            gregorian: {
              date: `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`,
              day: day
            },
            hijri: {
              day: hijri.day,
              month: {
                number: hijri.month,
                year: hijri.year
              }
            },
            isAdjusted: true,
            confirmed: hijri.confirmed
          });
        }
      }

      if (calendarDays.length === 0) {
        console.log(`⚠️ No Morocco month start data covers ${month}/${year}`);
        return null;
      }

      console.log(`✅ Generated ${calendarDays.length} days for Morocco ${month}/${year}`);
      return calendarDays;
    }

    // Saudi: Use Aladhan API (Umm al-Qura calculation)
    try {
      const endpoint = `https://api.aladhan.com/v1/gToHCalendar/${month}/${year}?adjustment=0`;
      console.log(`🇸🇦 Fetching Saudi calendar from API`);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.data) {
          return data.data.map(dayData => ({
            gregorian: {
              date: dayData.gregorian.date,
              day: parseInt(dayData.gregorian.day)
            },
            hijri: {
              day: parseInt(dayData.hijri.day),
              month: {
                number: parseInt(dayData.hijri.month.number),
                year: parseInt(dayData.hijri.year)
              }
            }
          }));
        }
      }
    } catch (error) {
      console.error('Aladhan API failed:', error.message);
    }

    return null;
  }, [calendarSource, currentDate, getMoroccoHijriDate]);

  const fetchIslamicData = useCallback(async (date) => {
    // Morocco: Calculate from month starts
    if (calendarSource === 'morocco') {
      const hijri = getMoroccoHijriDate(date);
      if (hijri) {
        return {
          day: hijri.day,
          month: {
            number: hijri.month,
            year: hijri.year
          }
        };
      }
      return null;
    }

    // Saudi: Use Aladhan API
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const dateString = `${day}-${month}-${year}`;

    try {
      const endpoint = `https://api.aladhan.com/v1/gToH/${dateString}?adjustment=0`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.data && data.data.hijri) {
          return {
            day: parseInt(data.data.hijri.day),
            month: {
              number: parseInt(data.data.hijri.month.number),
              year: parseInt(data.data.hijri.year)
            }
          };
        }
      }
    } catch (error) {
      console.log(`Aladhan API failed:`, error.message);
    }

    return null;
  }, [calendarSource, getMoroccoHijriDate]);

  const generateIslamicCalendarData = useCallback(async () => {
    // Try to get calendar data (Morocco from JSON, Saudi from API)
    const monthlyData = await fetchMonthlyIslamicCalendar();
    if (monthlyData && monthlyData.length > 0) {
      return monthlyData;
    }

    // Morocco: If no JSON data, return empty array (no fallback to API/calculation)
    // This ensures we only show confirmed moon sighting dates
    if (calendarSource === 'morocco') {
      console.log('🇲🇦 No Morocco sighting data available for this month');
      return [];
    }

    // Saudi only: Fallback to individual date fetching if monthly API fails
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const data = [];

    // Sample a few dates to get API data
    const sampleDates = [1, 10, 15, 25];
    const apiData = {};

    for (const day of sampleDates) {
      if (day <= daysInMonth) {
        const testDate = new Date(year, month, day);
        const apiResult = await fetchIslamicData(testDate);
        if (apiResult) {
          apiData[day] = apiResult;
        }
      }
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const gregorianDate = new Date(year, month, day);
      let islamicDate;

      if (apiData[day]) {
        islamicDate = apiData[day];
      } else {
        const calculated = calculateIslamicDate(gregorianDate);
        islamicDate = {
          day: calculated.day,
          month: {
            number: calculated.month,
            year: calculated.year
          }
        };
      }
      
      data.push({
        gregorian: {
          date: gregorianDate.toISOString().split('T')[0],
          day: day
        },
        hijri: islamicDate
      });
    }

    return data;
  }, [fetchMonthlyIslamicCalendar, calculateIslamicDate, fetchIslamicData, currentDate, calendarSource]);

  useEffect(() => {
    const updateCalendarData = async () => {
      setIsLoading(true);
      try {
        // Add a small delay to ensure loader is visible
        const [data] = await Promise.all([
          generateIslamicCalendarData(),
          new Promise(resolve => setTimeout(resolve, 200)) // Minimum 200ms to see loader
        ]);
        setIslamicCalendarData(data);
      } catch (error) {
        console.error('Error generating calendar data:', error);
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const fallbackData = [];

        for (let day = 1; day <= daysInMonth; day++) {
          const gregorianDate = new Date(year, month, day);
          const islamicDate = calculateIslamicDate(gregorianDate);
          
          fallbackData.push({
            gregorian: {
              date: gregorianDate.toISOString().split('T')[0],
              day: day
            },
            hijri: {
              day: islamicDate.day,
              month: {
                number: islamicDate.month,
                year: islamicDate.year
              }
            }
          });
        }
        setIslamicCalendarData(fallbackData);
      } finally {
        setIsLoading(false);
      }
    };

    updateCalendarData();
  }, [calendarSource, generateIslamicCalendarData, calculateIslamicDate, currentDate, getMoroccoAdjustmentStatus]);

  // Force re-render at midnight to update "today" highlighting and month transitions
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    
    const timeout = setTimeout(() => {
      const newDate = new Date();
      
      // Check if we've crossed into a new month
      if (currentDate.getMonth() !== newDate.getMonth() || currentDate.getFullYear() !== newDate.getFullYear()) {
        console.log('🗓️ Month changed from', currentDate.toDateString(), 'to', newDate.toDateString());
        setCurrentDate(newDate);
      }
      
      forceUpdate({});
      
      // Set up interval to update every day at midnight
      const interval = setInterval(() => {
        const intervalNewDate = new Date();
        
        // Use callback to compare against current state
        setCurrentDate(prevDate => {
          if (prevDate.getMonth() !== intervalNewDate.getMonth() || prevDate.getFullYear() !== intervalNewDate.getFullYear()) {
            console.log('🗓️ Month changed from', prevDate.toDateString(), 'to', intervalNewDate.toDateString());
            return intervalNewDate;
          }
          return prevDate;
        });
        
        forceUpdate({});
      }, 24 * 60 * 60 * 1000); // 24 hours
      
      return () => clearInterval(interval);
    }, timeUntilMidnight);
    
    return () => clearTimeout(timeout);
  }, [currentDate]);

  const getDaysInMonth = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Ensure calendar starts from Monday (0 = Monday, 6 = Sunday)
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  }, [currentDate]);

  const getIslamicDateForDay = (gregorianDay) => {
    const dayData = islamicCalendarData.find(item => item.gregorian.day === gregorianDay);
    return dayData ? dayData.hijri : null;
  };

  const getFastingDayLabels = (gregorianDay) => {
    if (!gregorianDay) return [];
    
    const hijriData = getIslamicDateForDay(gregorianDay);
    if (!hijriData) return [];
    
    const labels = [];
    const currentGregorianDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), gregorianDay);
    const dayOfWeek = currentGregorianDate.getDay();
    const islamicDay = hijriData.day;
    const islamicMonth = hijriData.month.number;

    // Forbidden fasting days (Sahih Muslim, Abu Sa'id al-Khudri):
    // Eid al-Fitr (1 Shawwal), Eid al-Adha (10 Dhul Hijjah),
    // and the three days of Tashreeq (11-13 Dhul Hijjah). Show a single
    // red pill and skip all Sunnah labels so e.g. 13 Dhul Hijjah doesn't
    // also get a White Day tag, and Eid falling on Mon/Thu doesn't get
    // a weekly-fast tag.
    if (islamicMonth === 10 && islamicDay === 1) {
      return [{ text: 'Eid al-Fitr', textMobile: 'Eid', className: 'fasting-label label-forbidden' }];
    }
    if (islamicMonth === 12 && islamicDay === 10) {
      return [{ text: 'Eid al-Adha', textMobile: 'Eid', className: 'fasting-label label-forbidden' }];
    }
    if (islamicMonth === 12 && islamicDay >= 11 && islamicDay <= 13) {
      return [{ text: 'Tashreeq', textMobile: 'Tshrq', className: 'fasting-label label-forbidden' }];
    }

    // Monday fasting
    if (dayOfWeek === 1) {
      labels.push({
        text: 'Monday',
        textMobile: 'Mon',
        className: 'fasting-label label-monday'
      });
    }
    
    // Thursday fasting
    if (dayOfWeek === 4) {
      labels.push({
        text: 'Thursday', 
        textMobile: 'Thu',
        className: 'fasting-label label-thursday'
      });
    }
    
    // White Days (13th, 14th, 15th of any Islamic month)
    if (islamicDay === 13 || islamicDay === 14 || islamicDay === 15) {
      labels.push({
        text: 'White Day',
        textMobile: 'White',
        className: 'fasting-label label-white-day'
      });
    }
    
    // Day of Arafah (9th Dhul Hijjah)
    if (islamicMonth === 12 && islamicDay === 9) {
      labels.push({
        text: 'Arafah',
        textMobile: 'Arafah',
        className: 'fasting-label label-arafah'
      });
    }
    
    // The Sunnah is to fast Ashura across two days to differ from other
    // traditions: the 9th + 10th (Tasu'a + Ashura) is preferred, or the
    // 10th + 11th. The 10th is the anchor; Tasu'a (9th) is shown as its
    // companion. The 10th+11th alternative is noted in the legend rather
    // than tagged, since the 11th has no traditional name.
    //
    // Tasu'a (9th Muharram) — companion to Ashura
    if (islamicMonth === 1 && islamicDay === 9) {
      labels.push({
        text: "Tasu'a",
        textMobile: "Tasu'a",
        className: 'fasting-label label-ashura-preferred'
      });
    }

    // Day of Ashura (10th Muharram)
    if (islamicMonth === 1 && islamicDay === 10) {
      labels.push({
        text: 'Ashura',
        textMobile: 'Ashura',
        className: 'fasting-label label-ashura'
      });
    }
    
    // First 9 days of Dhul Hijjah (1st-9th Dhul Hijjah)
    if (islamicMonth === 12 && islamicDay >= 1 && islamicDay <= 9) {
      labels.push({
        text: 'First 9 Days',
        textMobile: 'First 9',
        className: 'fasting-label label-first-nine'
      });
    }
    
    // 6 days of Shawwal (2nd-7th Shawwal, excluding Eid day)
    if (islamicMonth === 10 && islamicDay >= 2 && islamicDay <= 7) {
      labels.push({
        text: 'Shawwal',
        textMobile: 'Shwl',
        className: 'fasting-label label-shawwal'
      });
    }
    
    return labels;
  };

  const getShortMonthName = (monthNumber) => {
    const shortNames = [
      'Mhrm.', 'Safr.', 'Rab. I', 'Rab. II',
      'Jmd. I', 'Jmd. II', 'Rajb.', 'Shbn.', 
      'Rmdn.', 'Shwl.', 'Dhul-Q.', 'Dhul-H.'
    ];
    return shortNames[monthNumber - 1] || '';
  };

  const getFullMonthName = (monthNumber) => {
    const fullNames = [
      'Muharram', 'Safar', 'Rabi\' al-Awwal', 'Rabi\' al-Thani',
      'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', 'Sha\'ban',
      'Ramadan', 'Shawwal', 'Dhul Qa\'dah', 'Dhul Hijjah'
    ];
    return fullNames[monthNumber - 1] || '';
  };

  const days = useMemo(() => getDaysInMonth(), [getDaysInMonth]);
  const monthNames = useMemo(() => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ], []);

  const getCurrentIslamicMonth = () => {
    if (islamicCalendarData.length === 0) return null;
    
    // Get first and last days of the month
    const firstDay = islamicCalendarData[0];
    const lastDay = islamicCalendarData[islamicCalendarData.length - 1];
    
    if (!firstDay || !lastDay) return null;
    
    const firstMonth = firstDay.hijri.month;
    const lastMonth = lastDay.hijri.month;
    
    // Check if we have two different Islamic months
    if (firstMonth.number !== lastMonth.number || firstMonth.year !== lastMonth.year) {
      // Find which month is current based on today's date
      const today = new Date();
      const todayDay = today.getDate();
      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();
      
      // Check if today is in the current Gregorian month we're displaying
      const isCurrentMonth = todayMonth === currentDate.getMonth() && todayYear === currentDate.getFullYear();
      
      let currentMonthIsStart = true;
      if (isCurrentMonth) {
        // Find today's Islamic month data
        const todayData = islamicCalendarData.find(item => item.gregorian.day === todayDay);
        if (todayData) {
          // Check if today's Islamic month matches the end month
          currentMonthIsStart = !(todayData.hijri.month.number === lastMonth.number && todayData.hijri.month.year === lastMonth.year);
        }
      }
      
      return {
        isRange: true,
        startMonth: firstMonth,
        endMonth: lastMonth,
        currentMonthIsStart: currentMonthIsStart
      };
    }
    
    // Single month case - return the month from middle of Gregorian month
    const midMonthData = islamicCalendarData[Math.floor(islamicCalendarData.length / 2)];
    return {
      isRange: false,
      month: midMonthData ? midMonthData.hijri.month : null
    };
  };

  const currentIslamicMonth = getCurrentIslamicMonth();

  const Loader = () => (
    <div className="loader-container">
      <div className="loader"></div>
      <p className="loading-text">Loading calendar...</p>
    </div>
  );

  return (
    <div className="app-container">
      <div className="main-content">
        <div className="header">
          <a href="https://sunnahfasts.com" className="header-logo-link">
            <img src="/logo.svg" alt="Sunnah Fasting Calendar" className="header-logo" />
          </a>
          <h1>Sunnah fasting, simplified.</h1>
          <p>
            Know when to fast the Sunnah days—this calendar marks them on Gregorian dates, every month.
          </p>
          
          
          {/* <p>
            The calendar is based on the Islamic (Hijri) calendar and is updated at the start of every Gregorian month.
          </p> */}
        </div>

        <div className="card">
          <div className="tabs-container">
            <div className="tabs">
              <button
                onClick={() => {
                  setCalendarSource('morocco');
                  localStorage.setItem('calendarSource', 'morocco');
                }}
                className={`tab ${calendarSource === 'morocco' ? 'tab-active' : ''}`}
              >
                Local (UK, Morocco)
              </button>
              <button
                onClick={() => {
                  setCalendarSource('saudi');
                  localStorage.setItem('calendarSource', 'saudi');
                }}
                className={`tab ${calendarSource === 'saudi' ? 'tab-active' : ''}`}
              >
                Umm al-Qura (Saudi)
              </button>
            </div>
            
            {/* Desktop: Inline notification toggle */}
            {!isMobileBrowser() && ('Notification' in window) && (
              <div className="desktop-notification-toggle">
                <label className="toggle-label">
                  <span className="toggle-text">Get fasting reminders</span>
                  <div className="toggle-container">
                    <input 
                      type="checkbox" 
                      className="toggle-input"
                      checked={notificationsEnabled[calendarSource] && notificationPermission === 'granted'}
                      onChange={(e) => {
                        handleNotificationToggle(e.target.checked);
                      }}
                      disabled={notificationPermission === 'denied'}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
                
                
                {notificationPermission === 'denied' && (
                  <div className="permission-troubleshoot">
                    <p className="toggle-help">
                      Notifications blocked in browser settings
                    </p>
                    <button 
                      className="refresh-permission-btn"
                      onClick={() => {
                        setNotificationPermission(Notification.permission);
                      }}
                    >
                      Refresh Permission
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="calendar-container">
          <div className="date-display">
            <h2 className="gregorian-date">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            {currentIslamicMonth && (
              <p className="islamic-date">
                {currentIslamicMonth.isRange ? (
                  // Two months case: show slash format with current month highlighted
                  <>
                    {currentIslamicMonth.currentMonthIsStart ? (
                      <><span style={{fontWeight: 600}}>{getFullMonthName(currentIslamicMonth.startMonth.number)}</span> / {getFullMonthName(currentIslamicMonth.endMonth.number)} {currentIslamicMonth.endMonth.year}</>
                    ) : (
                      <>{getFullMonthName(currentIslamicMonth.startMonth.number)} / <span style={{fontWeight: 600}}>{getFullMonthName(currentIslamicMonth.endMonth.number)}</span> {currentIslamicMonth.endMonth.year}</>
                    )}
                  </>
                ) : (
                  // Single month case: show normal format  
                  <>
                    {getFullMonthName(currentIslamicMonth.month.number)} {currentIslamicMonth.month.year}
                  </>
                )}
              </p>
            )}
          </div>
          
          <div className="calendar-divider"></div>
          
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <div className="weekdays">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="calendar-grid">
                {days.map((day, index) => {
              if (!day) {
                return <div key={index} className="day-cell"></div>;
              }

              const fastingLabels = getFastingDayLabels(day);
              const hijriData = getIslamicDateForDay(day);
              const moroccoStatus = getMoroccoAdjustmentStatus(day);
              
              // Dynamic "today" check - always reflects actual current day
              const now = new Date();
              const isToday = day === now.getDate() && 
                            currentDate.getMonth() === now.getMonth() && 
                            currentDate.getFullYear() === now.getFullYear();

              return (
                <div
                  key={index}
                  className={`day-cell ${isToday ? 'today' : ''}`}
                >
                  <div className="day-number">{day}</div>
                  
                  {hijriData && (
                    <div className={`islamic-day ${
                      hijriData.day === 1 && moroccoStatus ? 
                        `islamic-day-new-month ${moroccoStatus}` : 
                        ''
                    }`}>
                      {hijriData.day === 1 ? (
                        <span className="islamic-month-underline">
                          <span className="islamic-day-desktop">
                            {hijriData.day} {getShortMonthName(hijriData.month.number)}{(moroccoStatus === 'uncertain' || moroccoStatus === 'provisional') && <span className="asterisk-orange"> *</span>}
                          </span>
                          <span className="islamic-day-mobile">
                            {getShortMonthName(hijriData.month.number)}{(moroccoStatus === 'uncertain' || moroccoStatus === 'provisional') && <span className="asterisk-orange"> *</span>}
                          </span>
                        </span>
                      ) : (
                        <>
                          <span className="islamic-day-desktop">{hijriData.day}</span>
                          <span className="islamic-day-mobile">{hijriData.day}</span>
                        </>
                      )}
                    </div>
                  )}

                  <div className="fasting-labels">
                    {fastingLabels.map((label, idx) => (
                      <div
                        key={idx}
                        className={label.className}
                      >
                        <span className="label-text-desktop">{label.text}</span>
                        <span className="label-text-mobile">{label.textMobile}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
                })}
              </div>
            </>
          )}
        </div>

        {/* Desktop notification toggle card for screens <900px */}
        {!isMobileBrowser() && ('Notification' in window) && (
          <div className="card desktop-notification-card">
            <div className="notification-toggle">
              <label className="toggle-label">
                <span className="toggle-text">Get fasting reminders</span>
                <div className="toggle-container">
                  <input 
                    type="checkbox" 
                    className="toggle-input"
                    checked={notificationsEnabled[calendarSource] && notificationPermission === 'granted'}
                    onChange={(e) => {
                      handleNotificationToggle(e.target.checked);
                    }}
                    disabled={notificationPermission === 'denied'}
                  />
                  <span className="toggle-slider"></span>
                </div>
              </label>
              
              
              {notificationPermission === 'denied' && (
                <div className="permission-troubleshoot">
                  <p className="toggle-help">
                    Notifications blocked in browser settings
                  </p>
                  <button 
                    className="refresh-permission-btn"
                    onClick={() => {
                      setNotificationPermission(Notification.permission);
                    }}
                  >
                    Refresh Permission
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {isMobileBrowser() && (
          <div className="card">
            <div className="notification-toggle">
                {/* Mobile Browser: Toggle that shows instructions */}
                  <label className="toggle-label">
                    <span className="toggle-text">Get fasting reminders</span>
                    <div className="toggle-container">
                      <input 
                        type="checkbox" 
                        className="toggle-input"
                        checked={showMobileInstructions}
                        onChange={(e) => {
                          setShowMobileInstructions(e.target.checked);
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </div>
                  </label>
                  
                  {showMobileInstructions && (
                    <div className="mobile-instructions-inline">
                      {(getPlatformInfo().isIOS || getPlatformInfo().isiPadOS) ? (
                        <>
                          <h4>Add to home screen to enable notifications</h4>
                          <ol className="instruction-steps">
                            <li>Tap the Share icon in your browser</li>
                            <li>Tap "Add to Home Screen"</li>
                            <li>Tap "Add" to confirm</li>
                            <li>Open the app from your home screen</li>
                            <li>Turn on fasting reminders in the app</li>
                          </ol>
                        </>
                      ) : (
                        <>
                          <h4>Add to home screen to enable notifications</h4>
                          <ol className="instruction-steps">
                            <li>Tap the menu (three dots) in Chrome</li>
                            <li>Tap "Add to Home screen"</li>
                            <li>Tap "Install" and then "Add"</li>
                            <li>Open the app from your home screen</li>
                            <li>Turn on fasting reminders in the app</li>
                          </ol>
                        </>
                      )}
                    </div>
                  )}
            </div>
          </div>
        )}


        <div className="legend">
          <h3>Sunnah Fasting Types</h3>
          <div className="legend-grid">
            <div className="legend-item">
              <div>
                <div className="legend-text">Monday & Thursday</div>
                <div className="legend-description">Recommended weekly fasting days</div>
              </div>
            </div>
            <div className="legend-item">
              <div>
                <div className="legend-text">White Days (13-15)</div>
                <div className="legend-description">Three middle days of each Islamic month</div>
              </div>
            </div>
            <div className="legend-item">
              <div>
                <div className="legend-text">Day of Arafah</div>
                <div className="legend-description">9th Dhul Hijjah - most virtuous fast</div>
              </div>
            </div>
            <div className="legend-item">
              <div>
                <div className="legend-text">Day of Ashura</div>
                <div className="legend-description">10th Muharram - expiates sins of past year. The Sunnah is to fast two days so as not to resemble other traditions: the 9th &amp; 10th (Tasu'a + Ashura, preferred) or the 10th &amp; 11th. Fasting the 10th alone is permissible but less complete.</div>
              </div>
            </div>
            <div className="legend-item">
              <div>
                <div className="legend-text">First 9 Days of Dhul Hijjah</div>
                <div className="legend-description">Most blessed days for fasting</div>
              </div>
            </div>
            <div className="legend-item">
              <div>
                <div className="legend-text">6 Days of Shawwal</div>
                <div className="legend-description">After Eid al-Fitr (2nd-7th Shawwal)</div>
              </div>
            </div>
            <div className="legend-item">
              <div>
                <div className="legend-text">Forbidden Days</div>
                <div className="legend-description">Eid al-Fitr (1 Shawwal), Eid al-Adha (10 Dhul Hijjah) and the three days of Tashreeq (11-13 Dhul Hijjah) — fasting is not permitted (Sahih Muslim)</div>
              </div>
            </div>
          </div>
        </div>

        <div className="logo-lockup-container">
          <a href="https://sunnahfasts.com" className="logo-lockup-link">
            <img src="/logo-lockup.svg" alt="Sunnah Fasts Logo" className="logo-lockup" />
          </a>
        </div>

        <div className="footer">
          <p className="footer-row">
            <span className="powered">Built by</span>
            <a
              href="https://longshotlabs.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              <span>Longshot Labs</span>
            </a>
          </p>
        </div>

      </div>
      <Analytics />
    </div>
  );
};

export default SunnahFastingCalendar;